/**
 * SoleadLab 2.0 — Task Entity API Client
 *
 * V4 Phase 2: Frontend service for issue + task entity reads + writes.
 *
 * Read switch: migrated sites read from entity APIs; non-migrated fall
 * back to blob.  Authority is the `migrated` flag from site_migrations.
 *
 * LOSSLESS PROJECTION (V4 Schema Amendment — migration 0020):
 * Dedicated columns store SQL-queryable fields (state, severity, etc.).
 * extras_json stores all rich/nested fields not covered by columns:
 *   Issues: assetPath, confidence, detectedAt, lastSeenAt, image,
 *     thermalSnapshot, thermalData, recommendedAction, estimatedImpact,
 *     suppression, advancedAlarmData, auditLog
 *   Tasks: linkedIssueIds (full array), assignees (full array), sla,
 *     schedule (full), estimatedImpact, checklist, workReport, auditLog
 *
 * Column fields are the primary query surface.
 * extras_json provides completeness for lossless round-trips.
 */

import type {
  Issue,
  Task,
  TasksConfig,
  AuditEntry,
  AssetPath,
  EstimatedImpact,
  IssueSuppression,
  AdvancedAlarmData,
  TaskAssignee,
  TaskSLA,
  TaskSchedule,
  WorkReport,
} from '@/types/tasks';

import { apiFetch, apiFetchWithEtag, ApiError } from './apiFetch';

// ─── Backend Row Types ──────────────────────────────────────────────────────

interface IssueRow {
  id: string;
  site_id: string;
  finding_id: string | null;
  source_type: string;
  source_ref_id: string;
  category: string;
  severity: string;
  ind_type: string;
  state: string;
  description: string | null;
  geo_json: string | null;
  asset_id: string | null;
  extras_json: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

interface TaskRow {
  id: string;
  site_id: string;
  issue_id: string | null;
  title: string;
  priority: string;
  status: string;
  assignee: string | null;
  scheduled_date: string | null;
  due_date: string | null;
  description: string | null;
  resolution_notes: string | null;
  completed_at: string | null;
  extras_json: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

interface ListResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  migrated: boolean;
}

// ─── Extras JSON Shapes ─────────────────────────────────────────────────────

/** Fields stored in issues.extras_json (not in dedicated columns) */
interface IssueExtras {
  assetPath?: AssetPath;
  confidence?: number;
  detectedAt?: string;
  lastSeenAt?: string;
  image?: string;
  thermalSnapshot?: string;
  thermalData?: unknown;
  recommendedAction?: string;
  estimatedImpact?: EstimatedImpact;
  suppression?: IssueSuppression;
  advancedAlarmData?: AdvancedAlarmData;
  auditLog?: AuditEntry[];
}

/** Fields stored in tasks.extras_json (not in dedicated columns) */
interface TaskExtras {
  linkedIssueIds?: string[];
  assignees?: TaskAssignee[];
  sla?: TaskSLA;
  schedule?: TaskSchedule;
  estimatedImpact?: EstimatedImpact;
  checklist?: string[];
  workReport?: WorkReport;
  auditLog?: AuditEntry[];
}

// ─── Result Types ──────────────────────────────────────────────────────────

export interface EntityIssueResult {
  migrated: boolean;
  issues: Issue[];
}

export interface EntityTaskResult {
  migrated: boolean;
  tasks: Task[];
}

export interface EntityTasksConfigResult {
  migrated: boolean;
  config: TasksConfig;
}

// ─── Fetch Utilities (thin adapters around centralized apiFetch) ──────────────

async function apiFetchGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path);
}

/**
 * Mutation adapter that preserves taskEntityApi's distinct 409 contract:
 * callers expect `Error("VERSION_CONFLICT")` with `.serverData` attached
 * (rather than the central `ApiError`). Local `isConflictError` checks
 * `err.message === 'VERSION_CONFLICT'`.
 */
async function apiFetchMutate<T>(
  path: string,
  method: 'POST' | 'PATCH' | 'DELETE',
  body?: unknown,
  version?: number,
): Promise<{ data: T; version: number }> {
  const headers: Record<string, string> = {};
  if (version !== undefined) headers['If-Match'] = String(version);

  try {
    const result = await apiFetchWithEtag<T>(path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return { data: result.data, version: result.etag };
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const conflict = new Error('VERSION_CONFLICT') as Error & {
        status: number;
        serverData: unknown;
      };
      conflict.status = 409;
      conflict.serverData = err.details;
      throw conflict;
    }
    throw err;
  }
}

// ─── Default Factories ─────────────────────────────────────────────────────

const EMPTY_ASSET_PATH: AssetPath = {};
const EMPTY_IMPACT: EstimatedImpact = { powerLossW: 0, energyLossKWh: 0, revenueLoss: 0 };
const EMPTY_AUDIT: AuditEntry[] = [];

// ─── Read Transforms (row → frontend shape) ───────────────────────────────

function parseExtras<T>(json: string | null): Partial<T> {
  if (!json) return {};
  try { return JSON.parse(json) as Partial<T>; }
  catch { return {}; }
}

/**
 * Backend issue row → frontend Issue shape.
 *
 * Dedicated columns provide queryable fields. extras_json restores all
 * rich/nested fields for a lossless round-trip. If extras_json is empty
 * (pre-migration data), safe defaults are used.
 */
function issueRowToIssue(row: IssueRow): Issue {
  let geo: { lng: number; lat: number } = { lng: 0, lat: 0 };
  if (row.geo_json) {
    try { geo = JSON.parse(row.geo_json); }
    catch { /* keep default */ }
  }

  const extras = parseExtras<IssueExtras>(row.extras_json);

  return {
    issueId: row.id,
    sourceType: row.source_type as Issue['sourceType'],
    sourceRefId: row.source_ref_id,
    siteId: row.site_id,
    assetPath: extras.assetPath ?? EMPTY_ASSET_PATH,
    geo,
    category: row.category,
    severity: row.severity as Issue['severity'],
    indType: row.ind_type,
    confidence: extras.confidence ?? 1.0,
    detectedAt: extras.detectedAt ?? row.created_at,
    lastSeenAt: extras.lastSeenAt ?? row.updated_at,
    state: row.state as Issue['state'],
    image: extras.image,
    thermalSnapshot: extras.thermalSnapshot,
    thermalData: extras.thermalData as Issue['thermalData'],
    recommendedAction: extras.recommendedAction ?? '',
    estimatedImpact: extras.estimatedImpact ?? EMPTY_IMPACT,
    notes: row.description ?? '',
    suppression: extras.suppression,
    advancedAlarmData: extras.advancedAlarmData,
    auditLog: extras.auditLog ?? EMPTY_AUDIT,
    _version: row.version,
  };
}

/**
 * Backend task row → frontend Task shape.
 *
 * Dedicated columns provide queryable fields. extras_json restores all
 * rich/nested fields for a lossless round-trip. If extras_json is empty,
 * column-derived values and safe defaults are used.
 */
function taskRowToTask(row: TaskRow): Task {
  const extras = parseExtras<TaskExtras>(row.extras_json);

  return {
    taskId: row.id,
    title: row.title,
    description: row.description ?? '',
    linkedIssueIds: extras.linkedIssueIds ?? (row.issue_id ? [row.issue_id] : []),
    priority: row.priority as Task['priority'],
    status: row.status as Task['status'],
    assignees: extras.assignees ?? (row.assignee ? [{ userId: row.assignee, role: '' }] : []),
    sla: extras.sla ?? {},
    schedule: extras.schedule ?? {
      start: row.scheduled_date ?? undefined,
      end: row.due_date ?? undefined,
    },
    estimatedImpact: extras.estimatedImpact ?? EMPTY_IMPACT,
    checklist: extras.checklist ?? [],
    workReport: extras.workReport ?? (row.completed_at ? { completedBy: '', completedAt: row.completed_at } : undefined),
    auditLog: extras.auditLog ?? EMPTY_AUDIT,
    _version: row.version,
  };
}

// ─── Write Transforms (frontend shape → API body) ─────────────────────────

/**
 * Pack an Issue into the shape expected by POST /issues.
 * Column-mapped fields go at top level; everything else into extras_json.
 */
export function issueToCreateBody(issue: Issue): Record<string, unknown> {
  const extras: IssueExtras = {
    assetPath: issue.assetPath,
    confidence: issue.confidence,
    detectedAt: issue.detectedAt,
    lastSeenAt: issue.lastSeenAt,
    image: issue.image,
    thermalSnapshot: issue.thermalSnapshot,
    thermalData: issue.thermalData,
    recommendedAction: issue.recommendedAction,
    estimatedImpact: issue.estimatedImpact,
    suppression: issue.suppression,
    advancedAlarmData: issue.advancedAlarmData,
    auditLog: issue.auditLog,
  };

  return {
    id: issue.issueId,
    source_type: issue.sourceType,
    source_ref_id: issue.sourceRefId,
    category: issue.category,
    severity: issue.severity,
    ind_type: issue.indType,
    state: issue.state,
    description: issue.notes || null,
    geo_json: JSON.stringify(issue.geo),
    extras_json: JSON.stringify(extras),
  };
}

/**
 * Build a PATCH body for an Issue partial update.
 * Maps frontend field names → column names + packs extras.
 */
export function issueToPatchBody(
  updates: Partial<Issue>,
  currentIssue: Issue,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  // Map column fields
  if (updates.sourceType !== undefined) patch.source_type = updates.sourceType;
  if (updates.sourceRefId !== undefined) patch.source_ref_id = updates.sourceRefId;
  if (updates.category !== undefined) patch.category = updates.category;
  if (updates.severity !== undefined) patch.severity = updates.severity;
  if (updates.indType !== undefined) patch.ind_type = updates.indType;
  if (updates.state !== undefined) patch.state = updates.state;
  if (updates.notes !== undefined) patch.description = updates.notes;
  if (updates.geo !== undefined) patch.geo_json = JSON.stringify(updates.geo);

  // Always repack extras from the merged issue so no fields are lost
  const merged = { ...currentIssue, ...updates };
  const extras: IssueExtras = {
    assetPath: merged.assetPath,
    confidence: merged.confidence,
    detectedAt: merged.detectedAt,
    lastSeenAt: merged.lastSeenAt,
    image: merged.image,
    thermalSnapshot: merged.thermalSnapshot,
    thermalData: merged.thermalData,
    recommendedAction: merged.recommendedAction,
    estimatedImpact: merged.estimatedImpact,
    suppression: merged.suppression,
    advancedAlarmData: merged.advancedAlarmData,
    auditLog: merged.auditLog,
  };
  patch.extras_json = JSON.stringify(extras);

  return patch;
}

/**
 * Pack a Task into the shape expected by POST /tasks.
 */
export function taskToCreateBody(task: Task): Record<string, unknown> {
  const extras: TaskExtras = {
    linkedIssueIds: task.linkedIssueIds,
    assignees: task.assignees,
    sla: task.sla,
    schedule: task.schedule,
    estimatedImpact: task.estimatedImpact,
    checklist: task.checklist,
    workReport: task.workReport,
    auditLog: task.auditLog,
  };

  return {
    id: task.taskId,
    title: task.title,
    description: task.description || null,
    issue_id: task.linkedIssueIds[0] ?? null,
    priority: task.priority,
    status: task.status,
    assignee: task.assignees[0]?.userId ?? null,
    scheduled_date: task.schedule.start ?? null,
    due_date: task.schedule.end ?? null,
    completed_at: task.workReport?.completedAt ?? null,
    extras_json: JSON.stringify(extras),
  };
}

/**
 * Build a PATCH body for a Task partial update.
 */
export function taskToPatchBody(
  updates: Partial<Task>,
  currentTask: Task,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  // Map column fields
  if (updates.title !== undefined) patch.title = updates.title;
  if (updates.description !== undefined) patch.description = updates.description;
  if (updates.priority !== undefined) patch.priority = updates.priority;
  if (updates.status !== undefined) patch.status = updates.status;
  if (updates.linkedIssueIds !== undefined) patch.issue_id = updates.linkedIssueIds[0] ?? null;
  if (updates.assignees !== undefined) patch.assignee = updates.assignees[0]?.userId ?? null;
  if (updates.schedule !== undefined) {
    patch.scheduled_date = updates.schedule.start ?? null;
    patch.due_date = updates.schedule.end ?? null;
  }
  if (updates.workReport !== undefined) {
    patch.completed_at = updates.workReport?.completedAt ?? null;
  }

  // Always repack extras from the merged task so no fields are lost
  const merged = { ...currentTask, ...updates };
  const extras: TaskExtras = {
    linkedIssueIds: merged.linkedIssueIds,
    assignees: merged.assignees,
    sla: merged.sla,
    schedule: merged.schedule,
    estimatedImpact: merged.estimatedImpact,
    checklist: merged.checklist,
    workReport: merged.workReport,
    auditLog: merged.auditLog,
  };
  patch.extras_json = JSON.stringify(extras);

  return patch;
}

// ─── Conflict Detection ───────────────────────────────────────────────────

export function isConflictError(err: unknown): boolean {
  return err instanceof Error && err.message === 'VERSION_CONFLICT';
}

// ─── Single-Item Fetch (for conflict reload) ────────────────────────────────

/**
 * Fetch a single issue by ID. Returns null on 404 or error.
 */
export async function fetchSingleEntityIssue(
  siteId: string,
  issueId: string,
): Promise<Issue | null> {
  try {
    const row = await apiFetchGet<IssueRow>(
      `/api/v2/sites/${siteId}/issues/${issueId}`,
    );
    return issueRowToIssue(row);
  } catch {
    return null;
  }
}

/**
 * Fetch a single task by ID. Returns null on 404 or error.
 */
export async function fetchSingleEntityTask(
  siteId: string,
  taskId: string,
): Promise<Task | null> {
  try {
    const row = await apiFetchGet<TaskRow>(
      `/api/v2/sites/${siteId}/tasks/${taskId}`,
    );
    return taskRowToTask(row);
  } catch {
    return null;
  }
}

// ─── Read API ──────────────────────────────────────────────────────────────

/**
 * Fetch ALL issues for a site from the entity API.
 * Auto-paginates. Returns issues + migration authority signal.
 */
export async function fetchEntityIssues(
  siteId: string,
): Promise<EntityIssueResult> {
  const allIssues: Issue[] = [];
  let page = 1;
  let migrated = false;
  const limit = 200;

  while (true) {
    const result = await apiFetchGet<ListResponse<IssueRow>>(
      `/api/v2/sites/${siteId}/issues?page=${page}&limit=${limit}`,
    );

    if (page === 1) {
      migrated = result.migrated;
    }

    for (const row of result.items) {
      allIssues.push(issueRowToIssue(row));
    }

    if (!result.hasMore) break;
    page++;
  }

  return { migrated, issues: allIssues };
}

/**
 * Fetch ALL tasks for a site from the entity API.
 * Auto-paginates. Returns tasks + migration authority signal.
 */
export async function fetchEntityTasks(
  siteId: string,
): Promise<EntityTaskResult> {
  const allTasks: Task[] = [];
  let page = 1;
  let migrated = false;
  const limit = 200;

  while (true) {
    const result = await apiFetchGet<ListResponse<TaskRow>>(
      `/api/v2/sites/${siteId}/tasks?page=${page}&limit=${limit}`,
    );

    if (page === 1) {
      migrated = result.migrated;
    }

    for (const row of result.items) {
      allTasks.push(taskRowToTask(row));
    }

    if (!result.hasMore) break;
    page++;
  }

  return { migrated, tasks: allTasks };
}

/**
 * Fetch both issues and tasks, returning a complete TasksConfig.
 * Merges the two `migrated` signals — both must be true for the
 * config to be considered authoritative.
 */
export async function fetchEntityTasksConfig(
  siteId: string,
): Promise<EntityTasksConfigResult> {
  const [issueResult, taskResult] = await Promise.all([
    fetchEntityIssues(siteId),
    fetchEntityTasks(siteId),
  ]);

  // Both entity types must be migrated for entity data to be authoritative
  const migrated = issueResult.migrated && taskResult.migrated;

  return {
    migrated,
    config: {
      issues: issueResult.issues,
      tasks: taskResult.tasks,
      auditLog: [], // global audit log not stored in entity tables
    },
  };
}

// ─── Write API ─────────────────────────────────────────────────────────────

/**
 * Create an issue via entity API.
 * Returns the created issue with _version from ETag.
 */
export async function createEntityIssue(
  siteId: string,
  issue: Issue,
): Promise<Issue> {
  const body = issueToCreateBody(issue);
  const { data, version } = await apiFetchMutate<IssueRow>(
    `/api/v2/sites/${siteId}/issues`,
    'POST',
    body,
  );
  const created = issueRowToIssue(data);
  created._version = version;
  return created;
}

/**
 * Update an issue via entity API with optimistic concurrency.
 * Returns the updated issue with new _version.
 */
export async function updateEntityIssue(
  siteId: string,
  issueId: string,
  updates: Partial<Issue>,
  currentIssue: Issue,
  currentVersion: number,
): Promise<Issue> {
  const body = issueToPatchBody(updates, currentIssue);
  const { data, version } = await apiFetchMutate<IssueRow>(
    `/api/v2/sites/${siteId}/issues/${issueId}`,
    'PATCH',
    body,
    currentVersion,
  );
  const updated = issueRowToIssue(data);
  updated._version = version;
  return updated;
}

/**
 * Delete an issue via entity API with optimistic concurrency.
 */
export async function deleteEntityIssue(
  siteId: string,
  issueId: string,
  currentVersion: number,
): Promise<void> {
  await apiFetchMutate<void>(
    `/api/v2/sites/${siteId}/issues/${issueId}`,
    'DELETE',
    undefined,
    currentVersion,
  );
}

/**
 * Create a task via entity API.
 * Returns the created task with _version from ETag.
 */
export async function createEntityTask(
  siteId: string,
  task: Task,
): Promise<Task> {
  const body = taskToCreateBody(task);
  const { data, version } = await apiFetchMutate<TaskRow>(
    `/api/v2/sites/${siteId}/tasks`,
    'POST',
    body,
  );
  const created = taskRowToTask(data);
  created._version = version;
  return created;
}

/**
 * Update a task via entity API with optimistic concurrency.
 * Returns the updated task with new _version.
 */
export async function updateEntityTask(
  siteId: string,
  taskId: string,
  updates: Partial<Task>,
  currentTask: Task,
  currentVersion: number,
): Promise<Task> {
  const body = taskToPatchBody(updates, currentTask);
  const { data, version } = await apiFetchMutate<TaskRow>(
    `/api/v2/sites/${siteId}/tasks/${taskId}`,
    'PATCH',
    body,
    currentVersion,
  );
  const updated = taskRowToTask(data);
  updated._version = version;
  return updated;
}

/**
 * Delete a task via entity API with optimistic concurrency.
 */
export async function deleteEntityTask(
  siteId: string,
  taskId: string,
  currentVersion: number,
): Promise<void> {
  await apiFetchMutate<void>(
    `/api/v2/sites/${siteId}/tasks/${taskId}`,
    'DELETE',
    undefined,
    currentVersion,
  );
}
